import {
  Button,
  Col,
  Flex,
  Form,
  FormProps,
  Input,
  message,
  Radio,
  Row,
  Tabs,
} from "antd";
import React, { FC, useEffect, useState } from "react";
import { IUploadReferral } from "../../../interfaces/IReferral";
import { IDocumentFields } from "../../../interfaces/ICase";
import {
  IClaimRes,
  PreAuthClaimField,
  PreAuthClaimResponse,
  PreAuthClaimSection,
} from "../../../interfaces/IPreAuthClaim";
import { renderCommonField } from "../CommonFields";
import {
  CheckOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { isEditable } from "@testing-library/user-event/dist/utils";
import { IDeficiencyCheck } from "../../../interfaces/IPatient";
import { useUserFunctionalities } from "../../../utils/UserFunctionalities";
import {
  getPortalData,
  getVisitStageStates,
} from "../../../models/preAuthSlice";
import { PropagateLoader } from "react-spinners";
import Loader from "../../../utils/Spinners";
import { AppDispatch, RootState } from "../../../store/store";
import { useDispatch, useSelector } from "react-redux";
import ManualStatus from "../PreAuth/ManualStatusButton";
import ClaimIDManuallyId from "../PreAuth/ClaimIDManuallyId";
import ManualPortalStatus from "../PreAuth/ManualPortalStatusButton";
import { WizardStep } from "../../../interfaces/IDischarge";
import ProceduresCoding from "./ProcedureList";

type FieldType = {
  additionalAmountPaid: boolean;
  amount: string;
};

const initialValues: FieldType = {
  additionalAmountPaid: false,
  amount: "",
};

type PreAuthClaimProps = {
  handleFileChange?: any;
  uploadFileModal: boolean;
  selectedFile: IUploadReferral;
  handleCancel: any;
  handleSave: CallableFunction;
  handleSaveToEdgePro: CallableFunction;
  handleUploadDocument: any;
  viewFile: CallableFunction;
  handleDeleteList: CallableFunction;
  preAuthClaimData: PreAuthClaimResponse | null;
  caseClaimSubmissionId: number | null;
  editedFields: Record<string, Record<string, string>>;
  onFieldChange: (sectionKey: string, fieldId: string, value: string) => void;
  visitType: string | undefined;
  uploadedDocumentUrl: Array<IDocumentFields>;
  handleSaveChangesIhx: CallableFunction;
  isLoading: boolean;
  isEditable: boolean;
  handleEditable: CallableFunction;
  handleSetDocumentUrl: CallableFunction;
  handleOpen: any;
  handleDownloadDocument: CallableFunction;
  isDownloadingDocument?: boolean;
  downloadingDocumentUrl?: string;
  isSubmitted: boolean;
  handleDeficiencyModal?: any;
  closeDeficiencyModal?: any;
  deficiencyModal: boolean;
  deficiencyQuestions?: any;
  previewDocumentUrl: string;
  setDocumentName: any;
  handleDeficiencyModalAfterSave: any;
  portalSubmission: string | null;
  nextStage: any;
  setMoveToNext: CallableFunction;
  allWizardSteps: WizardStep[];
  activeStep: number;
  handleTabsChange: any;
  isScreen: string;
  handleApprove: any;
  isApproved: boolean;
  isApprovedInitiated: boolean;
  cghsProcedures: any;
};

const PreAuthClaim: FC<PreAuthClaimProps> = (props) => {
  const dispatch = useDispatch<AppDispatch>();
  const [form] = Form.useForm();
  const [isAdditionalAmountPaid, setIsAdditionalAmountPaid] =
    useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isGovt, setIsGovt] = useState<boolean>(false);

  const patientStore = useSelector((state: RootState) => state.patients);
  const { stepperData, currentStep, stageAllState } = useSelector(
    (state: RootState) => state.preAuth
  );

  const currentStageDetails = stepperData?.currentVisitStage;
  const currentActiveStage = stepperData?.visitStages[currentStep];
  const customStatus =
    currentActiveStage?.step && currentStageDetails?.step
      ? currentActiveStage.step === currentStageDetails.step
        ? currentStageDetails.stateId
        : currentActiveStage.step > currentStageDetails.step
        ? 8
        : 4
      : null;

  useEffect(() => {
    if (
      patientStore?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
        "CGHS" ||
      patientStore?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
        "CAPF" ||
      patientStore?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
        "ECHS"
    ) {
      setIsGovt(true);
    }
  }, [patientStore?.fetchBasicDetails]);

  useEffect(() => {
    if (currentActiveStage?.visitStageId) {
      dispatch(
        getVisitStageStates({ visitStageId: currentActiveStage?.visitStageId })
      );
    }
  }, [currentActiveStage?.visitStageId]);

  const styles: Record<string, React.CSSProperties> = {
    mainContent: {
      minHeight: "64px",
      background: "rgb(243, 243, 243)",
      padding: "12px 25px", // Removed the semicolon and fixed the gap
      gap: "42px", // Added the gap as a separate property
      borderRadius: "16px 16px 0px 0px", // Fixed camelCase
      borderBottom: "1px solid rgb(166, 166, 166)",
      fontWeight: "600",
      fontSize: "16px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    statusDesign: {
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      gap: "20px",
      minWidth: "676px",
      maxHeight: "36px",
    },
    continueButton: {
      background: "#008B8B",
      border: "1px solid #008B8B",
      boxShadow: "0px 2px 8px 0px #00000040",
      borderRadius: "10px",
      padding: "12px 25px",
      textTransform: "uppercase",
      color: "white",
    },
    saveEditButton: {
      color: "white",
      background: "#008B8B",
      border: "1px solid #008B8B",
      boxShadow: "0px 2px 8px 0px #00000040",
      borderRadius: "10px",
      padding: "12px 16px",
    },
    editButton: {
      color: "#008B8B",
      background: "white",
      border: "1px solid #008B8B",
      boxShadow: "0px 2px 8px 0px #00000040",
      borderRadius: "10px",
      padding: "12px 16px",
    },
    previousButton: {
      background: "#008B8B",
      border: "1px solid #008B8B",
      boxShadow: "0px 2px 8px 0px #00000040",
      borderRadius: "10px",
      padding: "12px 25px",
      textTransform: "uppercase",
      color: "white",
      marginRight: "10px",
    },
    submitButton: {
      marginRight: "10px",
      background: "#008B8B",
      border: "1px solid #008B8B",
      boxShadow: "0px 2px 8px 0px #00000040",
      borderRadius: "10px",
      padding: "12px 25px",
      textTransform: "uppercase",
      color: "white",
    },
  };

  const validateForm = async () => {
    try {
      await form.validateFields({ validateOnly: true });
      setIsFormValid(true);
    } catch (errorInfo) {
      setIsFormValid(false);
    }
  };

  useEffect(() => {
    // Set form values from editedFields
    if (props?.editedFields?.cghsClaimSubmissionResponse) {
      const updatedFields = Object.keys(
        props?.editedFields.cghsClaimSubmissionResponse
      ).reduce((acc, key) => {
        if (key.toLowerCase().includes("date")) {
          acc[key] = props?.editedFields.cghsClaimSubmissionResponse[key]
            ? dayjs(
                props?.editedFields.cghsClaimSubmissionResponse[key],
                "DD-MM-YYYY HH:mm:ss"
              )
            : null;
        } else {
          acc[key] = props?.editedFields.cghsClaimSubmissionResponse[key]; // Retain the original value for other fields
        }
        return acc;
      }, {} as Record<string, any>);

      form.setFieldsValue(updatedFields);
    } else if (props?.editedFields?.ihxClaimSubmissionResponses) {
      form.setFieldsValue(props?.editedFields?.ihxClaimSubmissionResponses);
    } else if (props?.editedFields?.starHealthClaimSubmissionResponse) {
      form.setFieldsValue(
        props?.editedFields?.starHealthClaimSubmissionResponse
      );
    }

    // if (props?.editedFields?.cghsClaimSubmissionResponse) {
    //   form.setFieldsValue(props?.editedFields?.cghsClaimSubmissionResponse);
    // }
  }, [props?.editedFields, form]);

  useEffect(() => {
    if (!props?.isSubmitted) {
      validateForm();
    }
  }, [form, props?.editedFields]);

  const handleSubmit = async () => {
    if (props?.preAuthClaimData && !props?.deficiencyModal) {
      try {
        await form.validateFields();
        if (props?.preAuthClaimData["ihxClaimSubmissionResponses"]) {
          props?.handleSaveChangesIhx();
        } else if (props?.preAuthClaimData["cghsClaimSubmissionResponse"]) {
          props?.handleSave();
        } else {
          props?.handleSave();
        }
        // If validation passes, call onSaveChanges
      } catch (errorInfo) {
        message.error("Please fill all required fields before submitting.");
      }
    }
  };

  const handleSubmitForSavetoEdge = async () => {
    if (props?.preAuthClaimData && !props?.deficiencyModal) {
      try {
        await form.validateFields();
        if (props?.preAuthClaimData["ihxClaimSubmissionResponses"]) {
          props?.handleSaveChangesIhx();
        } else if (props?.preAuthClaimData["cghsClaimSubmissionResponse"]) {
          props?.handleSaveToEdgePro();
        } else if (
          props?.preAuthClaimData["starHealthClaimSubmissionResponse"]
        ) {
          props?.handleSaveToEdgePro();
        }
        // If validation passes, call onSaveChanges
      } catch (errorInfo) {
        message.error("Please fill all required fields before submitting.");
      }
    }
  };

  const handleEdit = () => {
    props?.handleEditable();
  };

  useEffect(() => {
    for (const sectionKey in props.editedFields) {
      if (props.editedFields[sectionKey]?.additionalAmountPaid === "true") {
        setIsAdditionalAmountPaid(true);
      } else {
        setIsAdditionalAmountPaid(false);
      }
    }
  }, [props?.editedFields]);

  useEffect(() => {
    form.setFieldsValue(props?.preAuthClaimData);
  }, [props?.preAuthClaimData]);

  const renderTabContent = () => {
    if (!props?.preAuthClaimData) {
      return <div>No data available</div>;
    }

    const responseKeys = [
      "ihxClaimSubmissionResponses",
      "cghsClaimSubmissionResponse",
      "echsClaimSubmissionResponse",
      "starHealthClaimSubmissionResponse",
      "vidalClaimSubmissionResponse",
    ];

    for (const key of responseKeys) {
      const data = props?.preAuthClaimData[key as keyof PreAuthClaimResponse];
      if (data && props?.visitType && key?.toLowerCase()?.includes("cghs")) {
        const sectionData = (data as PreAuthClaimSection[])[0]?.[
          props?.visitType?.toUpperCase()
        ];
        if (sectionData) {
          return (
            <Form
              form={form}
              // onFinish={handleSubmit}
              labelAlign="left"
              labelWrap
              labelCol={{
                xs: { span: 24 },
                sm: { span: 12 },
                md: { span: 8 },
                lg: { span: 6 },
                style: { fontWeight: "600" },
              }}
              wrapperCol={{
                xs: { span: 24 },
                sm: { span: 12 },
                md: { span: 16 },
                lg: { span: 18 },
              }}
              style={{ width: "100%", marginLeft: "0px" }}
            >
              {sectionData?.map((field: PreAuthClaimField, index: number) => (
                <Row style={{ width: "100%" }}>
                  {renderCommonField({
                    field,
                    index,
                    isReadOnly:
                      !props?.isEditable &&
                      props?.caseClaimSubmissionId &&
                      props?.portalSubmission
                        ? true
                        : !field.updatable,
                    onFieldChange: (_, fieldId, value) =>
                      props?.onFieldChange(key, fieldId, value),
                    handleFileChange: props?.handleFileChange,
                    value:
                      props?.editedFields[key]?.[field.id] ?? field.data ?? "",
                    key: key,
                    form: form,
                    isAdditionalAmountPaid:
                      field?.id == "amount" && field?.caseClaimSubmissionId
                        ? true
                        : isAdditionalAmountPaid,
                    caseSubmissionId: props?.caseClaimSubmissionId,
                    uploadedDocumentUrl: props?.uploadedDocumentUrl,
                    viewFile: props.viewFile,
                    handleDeleteList: props.handleDeleteList,
                    handleSetDocumentUrl: props?.handleSetDocumentUrl,
                    handleOpen: props?.handleOpen,
                    handleDownloadDocument: props?.handleDownloadDocument,
                    isDownloadingDocument: props?.isDownloadingDocument,
                    downloadingDocumentUrl: props?.downloadingDocumentUrl,
                    handleDeficiencyModal: props?.handleDeficiencyModal,
                    closeDeficiencyModal: props?.closeDeficiencyModal,
                    deficiencyModal: props?.deficiencyModal,
                    deficiencyQuestions: props?.deficiencyQuestions,
                    previewDocumentUrl: props?.previewDocumentUrl,
                    setDocumentName: props?.setDocumentName,
                    handleDeficiencyModalAfterSave:
                      props?.handleDeficiencyModalAfterSave,
                  })}
                </Row>
              ))}
            </Form>
          );
        }
      } else if (
        data &&
        props?.visitType &&
        key?.toLowerCase()?.includes("ihx")
      ) {
        const sectionData = data;

        if (sectionData) {
          return (
            <Form
              form={form}
              onFinish={handleSubmit}
              labelAlign="left"
              labelWrap
              labelCol={{
                xs: { span: 24 },
                sm: { span: 12 },
                md: { span: 8 },
                lg: { span: 6 },
              }}
              wrapperCol={{
                xs: { span: 24 },
                sm: { span: 12 },
                md: { span: 16 },
                lg: { span: 18 },
              }}
              style={{ width: "100%" }}
            >
              {sectionData[0]?.claimSubmissionDetails?.map(
                (field: PreAuthClaimField, index: number) => (
                  <Row style={{ width: "100%" }}>
                    {renderCommonField({
                      field,
                      index,
                      isReadOnly: !isEditable || !field.updatable,
                      onFieldChange: (_, fieldId, value) =>
                        props?.onFieldChange(key, fieldId, value),
                      handleFileChange: props?.handleFileChange,
                      value:
                        props?.editedFields[key]?.[field.id] ??
                        field.data ??
                        "",
                      key: key,
                      form: form,
                      isAdditionalAmountPaid: isAdditionalAmountPaid,
                      caseSubmissionId: props?.caseClaimSubmissionId,
                    })}
                  </Row>
                )
              )}
            </Form>
          );
        }
      } else if (data && key?.toLowerCase()?.includes("star")) {
        const sectionData = data;
        if (sectionData && Object.keys(sectionData).length > 0) {
          return (
            <Form
              form={form}
              onFinish={handleSubmit}
              labelAlign="left"
              labelWrap
              labelCol={{
                xs: { span: 24 },
                sm: { span: 12 },
                md: { span: 8 },
                lg: { span: 6 },
              }}
              wrapperCol={{
                xs: { span: 24 },
                sm: { span: 12 },
                md: { span: 16 },
                lg: { span: 18 },
              }}
              style={{ width: "100%" }}
            >
              {Object?.entries(sectionData[0])?.map(([keys, fields], i) => {
                return (
                  <div key={i}>
                    {Array.isArray(fields) && fields?.map((field, index) => (
                      <div key={field.id}>
                        {renderCommonField({
                          field,
                          index,
                          isReadOnly:
                            !props?.isEditable && props?.caseClaimSubmissionId
                              ? true
                              : !field.updatable,
                          onFieldChange: (_, fieldId, value) =>
                            props?.onFieldChange(key, fieldId, value),
                          handleFileChange: props?.handleFileChange,
                          value:
                            props?.editedFields[key]?.[field.id] ??
                            field.data ??
                            "",
                          key: key,
                          form: form,
                          isAdditionalAmountPaid:
                            field?.id == "amount" &&
                            field?.caseClaimSubmissionId
                              ? true
                              : isAdditionalAmountPaid,
                          caseSubmissionId: props?.caseClaimSubmissionId,
                          uploadedDocumentUrl: props?.uploadedDocumentUrl,
                          viewFile: props.viewFile,
                          handleDeleteList: props.handleDeleteList,
                          handleSetDocumentUrl: props?.handleSetDocumentUrl,
                          handleOpen: props?.handleOpen,
                          handleDownloadDocument: props?.handleDownloadDocument,
                          isDownloadingDocument: props?.isDownloadingDocument,
                          downloadingDocumentUrl: props?.downloadingDocumentUrl,
                          handleDeficiencyModal: props?.handleDeficiencyModal,
                          closeDeficiencyModal: props?.closeDeficiencyModal,
                          deficiencyModal: props?.deficiencyModal,
                          deficiencyQuestions: props?.deficiencyQuestions,
                          previewDocumentUrl: props?.previewDocumentUrl,
                          setDocumentName: props?.setDocumentName,
                          handleDeficiencyModalAfterSave:
                            props?.handleDeficiencyModalAfterSave,
                        })}
                      </div>
                    ))}
                  </div>
                );
              })}
            </Form>
          );
        }
      }
    }

    return <div>No data for this section</div>;
  };

  const { hasFunctionality, reloadFunctionalities } = useUserFunctionalities();
  useEffect(() => {
    // Optionally reload functionalities when component mounts
    reloadFunctionalities();
  }, [reloadFunctionalities]);

  return (
    <>
      {props?.preAuthClaimData ? (
        <>
          <div
            style={{
              border: "0.4px solid rgb(87, 87, 87)",
              gap: "15px",
              borderRadius: "16px",
              margin: "0px 0.5rem",
            }}
          >
            <div
              style={{
                minHeight: "64px",
                background: "rgb(243, 243, 243)",
                padding: "12px 35px", // Removed the semicolon and fixed the gap
                gap: "42px", // Added the gap as a separate property
                borderRadius: "16px 16px 0px 0px", // Fixed camelCase
                borderBottom: "1px solid rgb(166, 166, 166)",
                fontWeight: "600",
                fontSize: "16px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: "20px", alignContent: "center" }}>
                CLAIM SUBMISSION
              </span>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: "20px",
                  minWidth: "676px",
                  maxHeight: "36px",
                }}
              >
                <ClaimIDManuallyId />
                {currentActiveStage?.visitStageId &&
                  patientStore?.fetchBasicDetails?.visitInfoResponse?.id && (
                    <ManualStatus
                      stateData={
                        stageAllState?.[currentActiveStage?.visitStageId] ||
                        null
                      }
                      status={customStatus}
                      visitId={
                        patientStore?.fetchBasicDetails?.visitInfoResponse?.id
                      }
                      visitStageId={currentActiveStage?.visitStageId}
                      // isUpdateable={
                      //   customStatus == 4
                      //     ? false
                      //     : true
                      // }
                      isUpdateable={true}
                    />
                  )}
                {(patientStore?.fetchBasicDetails?.visitInfoResponse
                  ?.portalStatus ||
                  true) && (
                  <ManualPortalStatus
                    status={
                      patientStore?.fetchBasicDetails?.visitInfoResponse
                        ?.portalStatus || "-"
                    }
                    visitId={
                      patientStore?.fetchBasicDetails?.visitInfoResponse?.id
                    }
                    visitStageId={currentActiveStage?.visitStageId}
                    isUpdateable={
                      hasFunctionality("portalStatus") && !isGovt ? true : false
                    }
                  />
                )}
              </div>
            </div>
            {/* <div style={{ padding: "20px 45px 20px 25px" }}>
              <div
                className="claim-submission"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {props?.preAuthClaimData &&
                props?.caseClaimSubmissionId &&
                props?.portalSubmission === "save as draft" ? (
                  props?.isEditable ? (
                    <Button
                      shape="circle"
                      icon={<CloseCircleOutlined />}
                      onClick={(e) => {
                        handleEdit();
                        handleSubmitForSavetoEdge();
                      }}
                    />
                  ) : (
                    <Button
                      shape="circle"
                      icon={<EditOutlined />}
                      onClick={(e) => {
                        handleEdit();
                      }}
                    />
                  )
                ) : (
                  ""
                )}
              </div>
              <Row justify="start">{renderTabContent()}</Row>

              <Row justify="end">
                {(props.portalSubmission === "save as draft" ||
                  props.portalSubmission === null) && (
                  <>
                    {hasFunctionality("claimCreation") && (
                      <Form.Item style={{ marginBottom: "0px" }}>
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={props?.isLoading}
                          onClick={handleSubmitForSavetoEdge}
                          style={{
                            marginRight: "10px",
                            background: "#008B8B",
                            border: "1px solid #008B8B",
                            boxShadow: "0px 2px 8px 0px #00000040",
                            borderRadius: "10px",
                            padding: "12px 25px",
                            textTransform: "uppercase",
                            color: "white",
                          }}
                          //   disabled={!isFormValid}
                        >
                          {!props?.caseClaimSubmissionId
                            ? "SAVE AS DRAFT"
                            : "UPDATE AS DRAFT"}
                        </Button>
                      </Form.Item>
                    )}
                  </>
                )}

                <Form.Item style={{ marginBottom: "0px" }}>
                  {hasFunctionality("claimSubmission") &&
                    (props.portalSubmission === "save as draft" ||
                      props.portalSubmission === null) && (
                      <Button
                        style={{
                          background: "#008B8B",
                          border: "1px solid #008B8B",
                          boxShadow: "0px 2px 8px 0px #00000040",
                          borderRadius: "10px",
                          padding: "12px 25px",
                          textTransform: "uppercase",
                          color: "white",
                        }}
                        type="primary"
                        icon={<SaveOutlined />}
                        onClick={handleSubmit}
                        loading={props?.isLoading}
                      >
                        Submit on Portal
                      </Button>
                    )}
                </Form.Item>
                {(patientStore?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
                    "CGHS" || patientStore?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
                    "MEDI ASSIST" || patientStore?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
                    "STAR HEALTH") &&
                  !(
                    props?.portalSubmission === "save as draft" ||
                    props?.portalSubmission === null
                  ) && (
                    <Button
                      style={{
                        marginRight: "10px",
                        background: "#008B8B",
                        border: "1px solid #008B8B",
                        boxShadow: "0px 2px 8px 0px #00000040",
                        borderRadius: "10px",
                        padding: "12px 25px",
                        textTransform: "uppercase",
                        color: "white",
                      }}
                      onClick={() => {
                        props?.setMoveToNext(true);
                      }}
                    >
                      NEXT: {props?.nextStage?.visitStage}
                    </Button>
                  )}
              </Row>
            </div> */}
            {props?.isScreen == "CGHS" || props?.isScreen == "CAPF" ? (
              <>
                <Tabs
                  activeKey={props?.activeStep.toString()}
                  type="card"
                  size="large"
                  style={{ width: "100%", padding: "20px 25px 0px 25px" }}
                  onChange={props?.handleTabsChange}
                  items={props?.allWizardSteps?.map((step) => ({
                    label: step?.title,
                    key: step?.step?.toString(),
                    // children: renderTabContent(),
                    // disabled: editMode && !enabledTabs.includes(step?.step),
                  }))}
                />

                {props?.activeStep == 0 ? (
                  <>
                    <ProceduresCoding cghsProcedures={props?.cghsProcedures} />
                    <div
                      style={{
                        padding: "0px 25px 30px 0px",
                        textAlign: "right",
                      }}
                    >
                      <Button
                        style={styles.continueButton}
                        onClick={() => {
                          props?.handleTabsChange(props?.activeStep + 1);
                        }}
                      >
                        Continue
                      </Button>
                    </div>
                  </>
                ) : (
                  <div style={{ padding: "20px 25px 20px 25px" }}>
                    <div
                      className="claim-submission"
                      style={{
                        display: "flex",
                        justifyContent: "end",
                        alignItems: "center",
                        padding: "10px 0",
                      }}
                    >
                      {props?.preAuthClaimData &&
                      props?.caseClaimSubmissionId &&
                      props?.portalSubmission === "save as draft" ? (
                        props?.isEditable ? (
                          <Button
                            // shape="circle"
                            style={styles.saveButton}
                            icon={<SaveOutlined />}
                            onClick={(e) => {
                              handleEdit();
                              handleSubmitForSavetoEdge();
                            }}
                          >
                            Save
                          </Button>
                        ) : (
                          <Button
                            // shape="circle"
                            style={styles.editButton}
                            icon={<EditOutlined />}
                            onClick={(e) => {
                              handleEdit();
                            }}
                          >
                            Edit
                          </Button>
                        )
                      ) : (
                        ""
                      )}
                    </div>
                    <Row justify="start">{renderTabContent()}</Row>

                    <Row justify="end" style={{ marginTop: "16px" }}>
                      <Button
                        style={styles.previousButton}
                        onClick={() => {
                          props?.handleTabsChange(props?.activeStep - 1);
                        }}
                      >
                        PREVIOUS
                      </Button>

                      {(props.portalSubmission === "save as draft" ||
                        props.portalSubmission === null) && (
                        <>
                          {hasFunctionality("claimCreation") && (
                            <Form.Item style={{ marginBottom: "0px" }}>
                              <Button
                                type="primary"
                                htmlType="submit"
                                loading={props?.isLoading}
                                onClick={handleSubmitForSavetoEdge}
                                style={styles.submitButton}
                                //   disabled={!isFormValid}
                              >
                                {!props?.caseClaimSubmissionId
                                  ? "SAVE AS DRAFT"
                                  : "UPDATE AS DRAFT"}
                              </Button>
                            </Form.Item>
                          )}
                        </>
                      )}

                      <Form.Item style={{ marginBottom: "0px" }}>
                        {hasFunctionality("claimApprover") &&
                          (props.portalSubmission === "save as draft" ||
                            props.portalSubmission === null) && (
                            <Button
                              style={{
                                background: "#008B8B",
                                marginRight: "8px",
                                float: "right",
                              }}
                              disabled={props?.isApproved}
                              type="primary"
                              icon={<CheckOutlined />}
                              onClick={props?.handleApprove}
                              loading={props?.isApprovedInitiated}
                            >
                              Approve Claim
                            </Button>
                          )}
                      </Form.Item>

                      <Form.Item style={{ marginBottom: "0px" }}>
                        {hasFunctionality("claimSubmission") &&
                          (props.portalSubmission === "save as draft" ||
                            props.portalSubmission === null) && (
                            <Button
                              style={styles.submitButton}
                              type="primary"
                              icon={<SaveOutlined />}
                              onClick={handleSubmit}
                              loading={props?.isLoading}
                            >
                              Submit on Portal
                            </Button>
                          )}
                      </Form.Item>
                      {
                        !(
                          props?.portalSubmission === "save as draft" ||
                          props?.portalSubmission === null
                        ) && (
                          <Button
                            style={styles.submitButton}
                            onClick={() => {
                              props?.setMoveToNext(true);
                            }}
                          >
                            NEXT: {props?.nextStage?.visitStage}
                          </Button>
                        )}
                    </Row>
                  </div>
                )}
              </>
            ) : (
              <div style={{ padding: "20px 45px 20px 25px" }}>
                <div
                  className="claim-submission"
                  style={{
                    display: "flex",
                    justifyContent: "end",
                    alignItems: "center",
                  }}
                >
                  {props?.preAuthClaimData &&
                  props?.caseClaimSubmissionId &&
                  props?.portalSubmission === "save as draft" ? (
                    props?.isEditable ? (
                      <Button
                        // shape="circle"
                        style={styles.saveEditButton}
                        icon={<SaveOutlined />}
                        onClick={(e) => {
                          handleEdit();
                          handleSubmitForSavetoEdge();
                        }}
                      >
                        Save
                      </Button>
                    ) : (
                      <Button
                        // shape="circle"
                        style={styles.editButton}
                        icon={<EditOutlined />}
                        onClick={(e) => {
                          handleEdit();
                        }}
                      >
                        Edit
                      </Button>
                    )
                  ) : (
                    ""
                  )}
                </div>
                <Row justify="start">{renderTabContent()}</Row>

                <Row justify="end" style={{ marginTop: "16px" }}>
                  {(props.portalSubmission === "save as draft" ||
                    props.portalSubmission === null) && (
                    <>
                      {hasFunctionality("claimCreation") && (
                        <Form.Item style={{ marginBottom: "0px" }}>
                          <Button
                            type="primary"
                            htmlType="submit"
                            loading={props?.isLoading}
                            onClick={handleSubmitForSavetoEdge}
                            style={styles.submitButton}
                            //   disabled={!isFormValid}
                          >
                            {!props?.caseClaimSubmissionId
                              ? "SAVE AS DRAFT"
                              : "UPDATE AS DRAFT"}
                          </Button>
                        </Form.Item>
                      )}
                    </>
                  )}

                  <Form.Item style={{ marginBottom: "0px" }}>
                    {hasFunctionality("claimApprover") &&
                      (props.portalSubmission === "save as draft" ||
                        props.portalSubmission === null) && (
                        <Button
                          style={{
                            background: "#008B8B",
                            marginRight: "8px",
                            float: "right",
                          }}
                          disabled={props?.isApproved}
                          type="primary"
                          icon={<CheckOutlined />}
                          onClick={props?.handleApprove}
                          loading={props?.isApprovedInitiated}
                        >
                          Approve Claim
                        </Button>
                      )}
                  </Form.Item>

                  <Form.Item style={{ marginBottom: "0px" }}>
                    {hasFunctionality("claimSubmission") &&
                      (props.portalSubmission === "save as draft" ||
                        props.portalSubmission === null) && (
                        <Button
                          style={styles.submitButton}
                          type="primary"
                          icon={<SaveOutlined />}
                          onClick={handleSubmit}
                          loading={props?.isLoading}
                        >
                          Submit on Portal
                        </Button>
                      )}
                  </Form.Item>

                  {/* (patientStore?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
                    "CGHS" ||
                    patientStore?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
                      "MEDI ASSIST" ||
                    patientStore?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
                      "STAR HEALTH") && */}
                  {
                    !(
                      props?.portalSubmission === "save as draft" ||
                      props?.portalSubmission === null
                    ) && (
                      <Button
                        style={styles.submitButton}
                        onClick={() => {
                          props?.setMoveToNext(true);
                        }}
                      >
                        NEXT: {props?.nextStage?.visitStage}
                      </Button>
                    )}
                </Row>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <Row justify={"center"}>
            <Loader />
          </Row>
        </>
      )}
    </>
  );
};

export default PreAuthClaim;
